import _ from 'lodash'
import { format, parseISO } from 'date-fns'

const getLoincCode = lab =>
  lab?.code?.coding?.find(element => element?.system === 'http://loinc.org')
    ?.code || ''

const getLabCode = lab => _.get(lab, 'code.text', '')

const getLabCodeHistory = (lab, code) =>
  lab?.code?.coding?.find(element => element?.display === code)?.code || ''

const isNullOrUndefined = o => o === null || o === undefined

const isValidHistoryTestResult = result => {
  if (isNullOrUndefined(result) || result === '') {
    return false
  }
  const num = Number(result)

  return !isNaN(num) && num >= 0
}

const filterResources = data => {
  const entries = _.get(data, 'entry', [])
  if (entries === null) {
    return []
  }
  return entries.filter(
    ({ resource }) =>
      resource.status !== 'entered-in-error' &&
      _.get(resource, 'verificationStatus.coding[0].code') !==
        'entered-in-error' &&
      resource.resourceType !== 'OperationOutcome' &&
      _.get(resource, 'category[0].coding[0].code') === 'laboratory'
  )
}

const filterResourcesForImmunizations = data => {
  const entries = _.get(data, 'entry', [])
  if (entries === null) {
    return []
  }
  return entries.filter(
    ({ resource }) =>
      resource.resourceType === 'Immunization' &&
      resource.status === 'completed' &&
      _.get(resource, 'verificationStatus.coding[0].code') !==
        'entered-in-error'
  )
}

export const formatDateTime = (dateStr, formatStr = 'M/d/yyyy') => {
  if (!dateStr) {
    return '-'
  }

  const normalizedDate = `${dateStr}`?.includes('T')
    ? dateStr
    : `${dateStr}T00:00:00`

  const date = parseISO(new Date(normalizedDate).toISOString())

  if (formatStr) {
    return format(date, formatStr)
  }
  return date.toLocaleDateString()
}

const formatDateTestResultHistory = date => {
  const options = { month: 'short', day: 'numeric', year: 'numeric' }
  const formattedDate = date.toLocaleDateString('en-US', options)
  const [monthDay, year] = formattedDate.split(', ')
  return `${monthDay}\n${year}`
}

export const groupTestResultsByDate = tests => {
  const keys = Object.keys(tests)

  keys.sort((a, b) => {
    return new Date(b) - new Date(a)
  })

  const orderedObject = {}
  keys.forEach(key => {
    orderedObject[key] = tests[key]
  })

  return Object.values(orderedObject).map(test =>
    _.groupBy(test, item => item?.date)
  )
}

export const parseTestResults = data => {
  const tests = filterResources(data).map(({ resource }) => {
    const textRange = _.get(resource, 'referenceRange[0].text', '')
    const textResult = _.get(resource, 'valueCodeableConcept.text', '')
    const normalStatus = _.get(resource, 'interpretation[0].text', '')
    const low = _.get(resource, 'referenceRange[0].low.value', '')
    const high = _.get(resource, 'referenceRange[0].high.value', '')
    const units =
      _.get(resource, 'referenceRange[0].low.unit', '') ||
      _.get(resource, 'referenceRange[0].high.unit', '') ||
      _.get(resource, 'valueQuantity.unit', '')

    const isLowExist = !isNullOrUndefined(low) && low !== ''
    const isHighExist = !isNullOrUndefined(high) && high !== ''
    const isUnitsExist = !isNullOrUndefined(units) && units !== ''
    const dash = isLowExist && isHighExist ? '-' : ''

    const convertedDateTime = new Date(resource.effectiveDateTime)
    const isValidDate =
      resource.effectiveDateTime && !isNaN(convertedDateTime.getTime())
    const timeZoneAbbreviation = new Intl.DateTimeFormat('en-US', {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeZoneName: 'short'
    })
      .format(convertedDateTime)
      .split(' ')[1]
    const effectiveDateTime = isValidDate
      ? `${format(
          convertedDateTime,
          'M/d/yyyy h:mm aaa'
        )} ${timeZoneAbbreviation}`
      : '-'

    const rareText =
      resource.valueString && resource.valueString.toLowerCase() === 'none'
        ? 'rare'
        : ''

    const getRange = () => {
      return isLowExist || isHighExist || isUnitsExist
        ? `${low} ${dash} ${high} ${units}`
        : rareText
    }

    const getResult = () =>
      textResult
        ? normalStatus === 'Normal'
          ? textResult
          : `${textResult} (${normalStatus})`
        : (_.get(resource, 'valueQuantity.value') ??
          _.get(resource, 'valueString', ''))

    const code = getLabCode(resource)

    const codeHistory = getLabCodeHistory(resource, code)

    const loincCode = getLoincCode(resource)

    return {
      basedOn: _.get(resource, 'basedOn[0].display', ''),
      effectiveDateTime,
      date: formatDateTime(convertedDateTime),
      month: format(new Date(resource.effectiveDateTime), 'MMM'),
      day: format(new Date(resource.effectiveDateTime), 'dd'),
      year: format(new Date(resource.effectiveDateTime), 'yyyy'),
      code,
      codeHistory,
      loincCode,
      result: getResult(),
      status: _.get(resource, 'status', '-'),
      units,
      range: getRange(),
      textRange,
      encounterId: _.get(resource, 'encounter.reference', '').split('/')[1],
      low,
      high,
      id: _.get(resource, 'id')
    }
  })

  return _.groupBy(tests, item => item.date)
}

export const parseHistoryTestResults = data => {
  const tests = filterResources(data).map(({ resource }) => {
    const textRange = _.get(resource, 'referenceRange[0].text', '')
    const textResult = _.get(resource, 'valueCodeableConcept.text', '')
    const normalStatus = _.get(resource, 'interpretation[0].text', '')
    const low = _.get(resource, 'referenceRange[0].low.value', '')
    const high = _.get(resource, 'referenceRange[0].high.value', '')
    const units =
      _.get(resource, 'referenceRange[0].low.unit', '') ||
      _.get(resource, 'referenceRange[0].high.unit', '') ||
      _.get(resource, 'valueQuantity.unit', '')
    const isLowExist = !isNullOrUndefined(low) && low !== ''
    const isHighExist = !isNullOrUndefined(low) && high !== ''
    const isUnitsExist = !isNullOrUndefined(low) && units !== ''
    const dash = isLowExist && isHighExist ? '-' : ''

    const rareText =
      resource.valueString && resource.valueString.toLowerCase() === 'none'
        ? 'rare'
        : ''
    const quantity =
      _.get(resource, 'valueQuantity.value') ??
      _.get(resource, 'valueString', '')

    const getRange = () => {
      return isLowExist || isHighExist || isUnitsExist
        ? `${low} ${dash} ${high} ${units}`
        : rareText
    }

    const getResult = () =>
      textResult
        ? normalStatus === 'Normal'
          ? textResult
          : `${textResult} (${normalStatus})`
        : quantity

    const convertedDateTime = new Date(resource.effectiveDateTime)

    const isValidDate =
      resource.effectiveDateTime && !isNaN(convertedDateTime.getTime())

    const timeZoneAbbreviation = isValidDate
      ? new Intl.DateTimeFormat('en-US', {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timeZoneName: 'short'
        })
          .format(convertedDateTime)
          .split(' ')[1]
      : ''

    const effectiveDateTime = isValidDate
      ? `${format(
          convertedDateTime,
          'M/d/yyyy h:mm aaa'
        )} ${timeZoneAbbreviation}`
      : '-'

    const code = getLabCode(resource)

    const codeHistory = getLabCodeHistory(resource, code)

    return {
      effectiveDateTime,
      date: formatDateTestResultHistory(new Date(effectiveDateTime)),
      range: getRange(),
      result: getResult(),
      isNormal: normalStatus,
      low,
      high,
      quantity,
      textRange,
      codeHistory
    }
  })
  return _.orderBy(
    _.filter(tests, item => isValidHistoryTestResult(item.quantity)),
    [item => new Date(item.effectiveDateTime).getTime()],
    ['asc']
  )
}

export const parseHealthLibrary = data => {
  const allDataEmpty = _.every(data, _.isEmpty)
  if (allDataEmpty) {
    return []
  }
  const deduplicatedData = _.uniqBy(data, 'id')
  return deduplicatedData.map(item => ({
    regularTitle: _.get(item, 'metadata.regularTitle', ''),
    nativeLanguageTitle: _.get(item, 'metadata.nativeLanguageTitle', ''),
    blurb: _.get(item, 'metadata.blurb', ''),
    body: _.get(item, 'body', ''),
    copyright: _.get(item, 'metadata.copyright', '')
  }))
}

export const parseImmunizations = data => {
  const filteredImmunizations = filterResourcesForImmunizations(data).map(
    ({ resource }) => ({
      title: _.get(resource, 'vaccineCode.text'),
      date: formatDateTime(resource.occurrenceDateTime)
    })
  )
  const groupedImmunizations = _.groupBy(
    filteredImmunizations,
    immunization => immunization.title
  )

  return _.map(groupedImmunizations, (value, key) => {
    const date = value
      .map(item => item.date)
      .reverse()
      .join(', ')
    return { title: key, date }
  })
}
